import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { selectUser } from "../../../store/userSlice";
import { getCities, getRegions, requestVehicle, RequestVehiclePayloadI } from "../../../utils/fetcher";
import InputSelect, { SelectOption } from "../../UI/Inputs/InputSelect";
import Modal from "../../UI/Modal";
import Button from "../../UI/Button";
import { moduleToSelectOptions } from "../FindVehicleFilter/utils";
import InputField from "../../UI/Inputs/InputField";
import { convertToEnglishNumbers, FormatPrice, rawPhoneNumber } from "../../../utils/generalUtils";
import { VehicleNames } from "../../../utils/GeneralTypes";
import { Success, Warning } from "../../Icons";
import LoadingSkeleton from "../../UI/LoadingSkeleton";
import style from "./index.module.css";
import InputRadio from "../../UI/Inputs/InputRadio";
import { useTranslation } from "../../../hooks/useTranslation";

type componentDataT = {
  cities: SelectOption[];
  citiesDependants: any[];
  regions: SelectOption[];
};

type FormStateT = {
  fleet: "0" | "1";
  name: string;
  phone: string;
  city: string;
  region_id: string;
  fleet_name?: string;
  position?: string;
  number_of_cars: number;
  cash: "0" | "1";
};

const RequestVehicle = ({
  vehicleID,
  vehicleName,
  vehicleClass,
  vehiclePrice,
  setIsRequestingModalActive,
  vehicleNames,
}: ComponentPropsType) => {
  const { user } = useSelector(selectUser);
  const { locale } = useTranslation();

  const [componentData, setComponentData] = useState<{
    isFetching: boolean;
    data: componentDataT;
  }>({
    isFetching: true,
    data: {
      cities: [],
      citiesDependants: [],
      regions: [],
    },
  });

  const [submitState, setSubmitState] = useState<{
    isSubmitting: boolean;
    error: string;
    success: boolean;
  }>({
    isSubmitting: false,
    error: "",
    success: false,
  });

  const { handleSubmit, watch, register, setValue, formState } = useForm<FormStateT>({
    defaultValues: {
      fleet: "0",
      name: user?.name || "",
      phone: user?.phone || "",
      city: "no_option",
      region_id: "no_option",
      fleet_name: "",
      position: "",
      number_of_cars: 1,
      cash: "1",
    },
  });

  const watchedInputs = watch();

  useEffect(() => {
    const data: componentDataT = {
      cities: [],
      citiesDependants: [],
      regions: [],
    };
    (async () => {
      const res = await Promise.all([getCities(), getRegions()]);
      const cities = moduleToSelectOptions(res[0].data.cities, [["name", ""], "id", ""], ["region_id"]);
      data.cities = cities.options;
      // @ts-ignore
      data.citiesDependants = cities.dependantConditions;
      data.regions = moduleToSelectOptions(res[1].data.regions, [["name", ""], "id", ""], null, ["city"]).options;

      setComponentData({
        isFetching: false,
        data,
      });
    })();
  }, []);

  const onRequestVehicleHandler = useCallback(
    async (data: FormStateT) => {
      setSubmitState({
        isSubmitting: true,
        error: "",
        success: false,
      });
      const submittedData: RequestVehiclePayloadI = {
        phone: "+20" + rawPhoneNumber(convertToEnglishNumbers(data.phone)),
        name: data.name,
        region_id: +data.region_id,

        fleet: data.fleet === "1",
        fleet_name: data.fleet === "1" ? data.fleet_name : undefined,
        number_of_cars: data.fleet === "1" ? data.number_of_cars : 1,
        cash: data.cash === "1",
      };

      try {
        await requestVehicle(vehicleNames.single, vehicleID, submittedData);
        setSubmitState({
          isSubmitting: false,
          success: true,
          error: "",
        });
      } catch (error: any) {
        setSubmitState({
          isSubmitting: false,
          success: false,
          error: error?.response?.data?.message || locale("error_occured"),
        });
      }
    },
    [vehicleNames, vehicleID, locale],
  );

  const onCloseResponseModal = useCallback(
    (success: boolean) => {
      if (success) {
        setIsRequestingModalActive(false);
        setSubmitState({ isSubmitting: false, error: "", success: false });
      } else setSubmitState({ isSubmitting: false, error: "", success: false });
    },
    [setIsRequestingModalActive],
  );

  return (
    <>
      {(submitState.success || submitState.error) && (
        <Modal onClose={() => onCloseResponseModal(submitState.success)}>
          <div className={style.modal}>
            <div className={style.modal_response}>
              {submitState.success ? (
                <Success width="50" height="50" color="#35b545" />
              ) : (
                <Warning width="50" height="50" color="#dc0000" />
              )}
              <p>{submitState.success ? locale("requested_successfuly") : submitState.error}</p>
            </div>
          </div>
        </Modal>
      )}
      {!submitState.success && !submitState.error && (
        <Modal onClose={() => (!submitState.isSubmitting ? setIsRequestingModalActive(false) : {})}>
          <div className={style.modal}>
            {componentData.isFetching ? (
              <div style={{ marginTop: "20px" }}>
                <LoadingSkeleton count={15} />
              </div>
            ) : (
              <>
                <div className={style.modal_header}>
                  <h3 className={style.modal_title}>{locale("confirm_request")}</h3>
                  <h3>
                    {vehicleName.split(" ").map((v, index, array) => (
                      <span
                        key={index}
                        style={{
                          display: "inline-block",
                          unicodeBidi: "embed",
                          marginRight: "0.25em",
                        }}
                      >
                        {v}
                      </span>
                    ))}
                  </h3>
                  {vehicleClass && <p>{vehicleClass}</p>}
                  <p style={{ color: "#dc0000" }}>{<FormatPrice value={+vehiclePrice} />}</p>
                </div>
                <form onSubmit={handleSubmit(onRequestVehicleHandler)}>
                  <div className={style.modal_inputs}>
                    <div
                      style={{
                        width: "fit-content",
                        marginLeft: "auto",
                        marginRight: "auto",
                        marginTop: "10px",
                      }}
                    >
                      <InputRadio
                        combined={true}
                        props={register("fleet")}
                        children={[
                          {
                            name: locale("individuals"),
                            value: 0,
                          },
                          {
                            name: locale("fleets"),
                            value: 1,
                          },
                        ]}
                      />
                    </div>
                    {!user && (
                      <>
                        <InputField
                          label={locale("name")}
                          type="text"
                          extraProps={register("name", {
                            required: locale("required_field"),
                          })}
                          error={
                            typeof formState?.errors?.name?.message === "string"
                              ? formState?.errors?.name?.message
                              : undefined
                          }
                        />
                        <InputField
                          label={locale("phone")}
                          type="phone"
                          extraProps={register("phone", {
                            required: locale("required_field"),
                          })}
                          error={
                            typeof formState?.errors?.phone?.message === "string"
                              ? formState?.errors?.phone?.message
                              : undefined
                          }
                        />
                      </>
                    )}

                    <InputSelect
                      props={{
                        ...register("city", {
                          validate: (val) => val !== "no_option" || locale("required_field"),
                        }),
                        value: watchedInputs.city,
                      }}
                      selectOptions={componentData.data.cities}
                      label={locale("city")}
                      setValue={setValue}
                      watchedInputs={watchedInputs}
                      dependantConditions={componentData.data.citiesDependants}
                      error={formState?.errors?.city?.message}
                    />
                    <InputSelect
                      props={{
                        ...register("region_id", {
                          validate: (val) => val !== "no_option" || locale("required_field"),
                        }),
                        value: watchedInputs.region_id,
                      }}
                      selectOptions={componentData.data.regions}
                      label={locale("region")}
                      watchedInputs={watchedInputs}
                      error={formState?.errors?.region_id?.message}
                    />
                    {watchedInputs.fleet === "1" ? (
                      <>
                        <InputField label={locale("fleet_name")} type="text" extraProps={register("fleet_name")} />
                        <InputField
                          label={`${locale("number_of")}`}
                          type="number"
                          extraProps={{
                            ...register("number_of_cars"),
                            min: "1",
                          }}
                        />
                      </>
                    ) : null}

                    <InputRadio
                      props={register("cash")}
                      children={[
                        {
                          name: locale("cash"),
                          value: "1",
                        },
                        {
                          name: locale("installment"),
                          value: "0",
                        },
                      ]}
                    />
                  </div>
                  <div className={style.modal_ctas}>
                    <Button
                      styleType="primary"
                      extraProps={{ style: { minWidth: "200px" } }}
                      buttonType="submit"
                      disabled={submitState.isSubmitting}
                      isLoading={submitState.isSubmitting}
                    >
                      {locale("confirm")}
                    </Button>
                  </div>
                </form>
              </>
            )}
          </div>
        </Modal>
      )}
    </>
  );
};

export default RequestVehicle;

type ComponentPropsType = {
  vehicleID: string;
  vehicleName: string;
  vehicleClass: string;
  vehiclePrice: string;
  setIsRequestingModalActive: CallableFunction;
  vehicleNames: VehicleNames;
};
